import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { SITE_URL } from '@/components/feature/SEOHead';

const services = [
  {
    id: 'claim',
    icon: 'ri-file-list-3-line',
    title: 'Report a Claim',
    desc: 'If you\'ve experienced a loss, our team will guide you through the claims process quickly and efficiently. We\'re here to help you every step of the way.',
    steps: [
      'Contact our office or fill out a claim form',
      'We\'ll connect you with your carrier',
      'Our team follows up throughout the process',
    ],
  },
  {
    id: 'auto-id',
    icon: 'ri-id-card-line',
    title: 'Request Auto ID Card',
    desc: 'Need a copy of your auto insurance ID card? Submit a request and we\'ll get it to you as quickly as possible via email or mail.',
    steps: [
      'Submit your request with policy details',
      'We verify your information',
      'Your ID card is sent to you promptly',
    ],
  },
  {
    id: 'certificate',
    icon: 'ri-folder-open-line',
    title: 'Request a Certificate',
    desc: 'Need a certificate of insurance for a contract or lender? We can issue certificates of insurance quickly to meet your business needs.',
    steps: [
      'Submit the certificate request with required info',
      'We prepare and issue the certificate',
      'Delivered directly to you or a third party',
    ],
  },
  {
    id: 'policy-change',
    icon: 'ri-edit-box-line',
    title: 'Request Policy Change',
    desc: 'Life changes — so should your coverage. Whether you\'re adding a new vehicle, updating your address, or adjusting limits, we make policy changes easy.',
    steps: [
      'Tell us what needs to change',
      'We review options with your carrier',
      'Changes are confirmed and documented',
    ],
  },
];

const serviceCenterJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Service Center | White Oak Insurance Group',
    description:
      'Access policy services at White Oak Insurance Group in Hattiesburg, MS. Report a claim, request an auto ID card, get a certificate of insurance, or request a policy change.',
    url: `${SITE_URL}/service-center`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'White Oak Insurance Group',
      url: SITE_URL,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Center', item: `${SITE_URL}/service-center` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I report a claim with White Oak Insurance Group?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Contact our office or submit a claim request through our Service Center. We will connect you with your carrier and follow up throughout the process.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I get a copy of my auto insurance ID card?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Submit a request through our Service Center with your policy details. We will verify your information and send your ID card promptly via email or mail.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I request a certificate of insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Submit the certificate request with the required information through our Service Center. We prepare and issue the certificate, then deliver it directly to you or a third party.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I request a policy change?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Tell us what needs to change through our Service Center. We review options with your carrier and confirm the changes with documentation.',
        },
      },
    ],
  },
];

export default function ServiceCenterPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Service Center | White Oak Insurance Group – Hattiesburg, MS"
        description="Manage your insurance policies with White Oak Insurance Group. Report a claim, request an auto ID card, obtain a certificate of insurance, or make a policy change in Hattiesburg, MS."
        keywords="insurance service center Hattiesburg MS, report insurance claim Mississippi, auto ID card request, certificate of insurance, policy change White Oak Insurance"
        canonical="/service-center"
        jsonLd={serviceCenterJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-[#0b2240]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b2240] via-[#0b2240] to-[#3072b7]/30" />
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 text-white text-center">
          <h1 className="text-5xl font-bold mb-4">Service Center</h1>
          <div className="w-16 h-1 bg-[#9cbde5] mx-auto mb-5" />
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            Already a client? We're here to help you manage your policies, report claims, and more.
          </p>
        </div>
      </section>

      {/* Quick Action Grid */}
      <section className="py-6 bg-white shadow-sm">
        <div className="max-w-screen-xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {services.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="flex flex-col items-center gap-2 text-center px-4 py-5 rounded-lg hover:bg-[#f8f9fc] border border-gray-100 hover:border-[#9cbde5] transition-all"
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <i className={`${s.icon} text-2xl text-[#3072b7]`} />
              </div>
              <span className="text-xs font-bold text-[#0b2240]">{s.title}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Service Sections */}
      <main className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 space-y-16">
          {services.map((service, i) => (
            <div
              key={service.id}
              id={service.id}
              className={`flex flex-col lg:flex-row gap-10 items-start ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            >
              {/* Icon Card */}
              <div className="flex-shrink-0 w-full lg:w-56">
                <div className="bg-[#0b2240] rounded-xl p-10 flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className={`${service.icon} text-4xl text-[#9cbde5]`} />
                  </div>
                  <h3 className="text-white font-bold text-base">{service.title}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 border-t-4 border-[#3072b7] bg-white shadow-sm rounded-b-xl px-8 py-8">
                <h3 className="text-2xl font-bold text-[#0b2240] mb-3">{service.title}</h3>
                <div className="w-10 h-0.5 bg-[#3072b7] mb-5" />
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{service.desc}</p>
                <h4 className="text-sm font-bold text-[#0b2240] mb-3">How It Works:</h4>
                <ol className="space-y-2 mb-6">
                  {service.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3072b7] text-white text-xs flex items-center justify-center font-bold mt-0.5">
                        {idx + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
                <Link
                  to="/contact"
                  className="inline-block bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-8 py-3 rounded-md transition-colors whitespace-nowrap"
                >
                  Submit a Request
                </Link>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Refer a Friend Banner */}
      <section className="bg-[#f8f9fc] py-16">
        <div className="max-w-screen-xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-[#0b2240] mb-3">Happy with Our Service?</h2>
          <p className="text-gray-500 mb-7 max-w-lg mx-auto">
            Refer a friend and help them get the same great coverage and service you enjoy.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-8 py-3.5 rounded-md transition-colors whitespace-nowrap"
            >
              Refer a Friend
            </Link>
            <Link
              to="/about#reviews"
              className="border-2 border-[#0b2240] text-[#0b2240] hover:bg-[#0b2240] hover:text-white font-bold text-sm px-8 py-3.5 rounded-md transition-colors whitespace-nowrap"
            >
              Leave a Review
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
